import React, { useState } from 'react';
import styled from 'styled-components';
import { mediaMSmMax } from 'helpers/breakpoints';
import { FlexCol } from 'components/primitives/flex';
import { useBreakpoint } from 'components/BreakpointProvider';
import { useSignUp } from '@clerk/nextjs';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import { authPasswordShowAtom } from 'auth/atoms';
import { useAtom } from 'jotai';
import LoadingSpinner from 'components/loading/LoadingSpinner';
import { useSearchParams } from 'next/navigation';
import useActivateSession from 'hooks/useActivateSession';
import useLazySnackbar from 'components/banners/useLazySnackbar';

const SocialLoginButtonsV2 = ({
  compact = false,
  ignoreSmallScreen = false,
}: {
  compact?: boolean;
  ignoreSmallScreen?: boolean;
}) => {
  const { signUp } = useSignUp();
  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;
  const rudderTrack = useRudderStackTrack();
  const [showPasswordInput] = useAtom(authPasswordShowAtom);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [isFacebookSubmitting, setIsFacebookSubmitting] = useState(false);
  const [isAppleSubmitting, setIsAppleSubmitting] = useState(false);
  const params = useSearchParams();
  const activateSession = useActivateSession();
  const { enqueueSnackbar } = useLazySnackbar();

  const redirectTo = params.get('redirect_to');
  const redirectUrl = redirectTo
    ? `/a/auth/sso-callback?redirect_to=${encodeURIComponent(redirectTo)}`
    : '/a/auth/sso-callback';

  const trackObject = showPasswordInput ? 'log_in_form' : 'log_in_or_sign_up_form';
  const redirectUrlComplete = redirectTo
    ? `/a/auth/sso-complete-account?track_object=${trackObject}&sso=true&redirect_to=${encodeURIComponent(redirectTo)}`
    : `/a/auth/sso-complete-account?track_object=${trackObject}&sso=true`;

  const handleError = async (error) => {
    const errorCode = error?.errors?.[0]?.code;
    if (errorCode === 'session_exists') {
      console.warn('Session already exists, activating session...', { redirectTo });

      await activateSession({ redirectTo });

      return enqueueSnackbar("You're already signed in", {
        variant: 'success',
        autoHideDuration: 5000,
      });
    }

    console.error(error);
  };

  const onGoogleClick = async () => {
    try {
      setIsGoogleSubmitting(true);
      rudderTrack('log_in_or_sign_up_with_social', {
        object: trackObject,
      });
      rudderTrack('click', {
        component_type: 'button',
        object: 'continue_with_google',
        context: trackObject,
      });
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl,
        redirectUrlComplete,
      });
    } catch (e) {
      await handleError(e);
      setIsGoogleSubmitting(false);
    }
  };

  const onFacebookClick = async () => {
    try {
      setIsFacebookSubmitting(true);
      rudderTrack('log_in_or_sign_up_with_social', {
        object: trackObject,
      });
      rudderTrack('click', {
        component_type: 'button',
        object: 'continue_with_facebook',
        context: trackObject,
      });
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_facebook',
        redirectUrl,
        redirectUrlComplete,
      });
    } catch (e) {
      await handleError(e);
      setIsFacebookSubmitting(false);
    }
  };

  const onAppleClick = async () => {
    try {
      setIsAppleSubmitting(true);
      rudderTrack('log_in_or_sign_up_with_social', {
        object: trackObject,
      });
      rudderTrack('click', {
        component_type: 'button',
        object: 'continue_with_apple',
        context: trackObject,
      });
      await signUp.authenticateWithRedirect({
        strategy: 'oauth_apple',
        redirectUrl,
        redirectUrlComplete,
      });
    } catch (e) {
      await handleError(e);
      setIsAppleSubmitting(false);
    }
  };

  return (
    <ButtonsWrapper>
      <Or>
        <hr />
        <span> or </span>
        <hr />
      </Or>
      <ButtonWrapper gap="20px" className={(ignoreSmallScreen ? false : smallScreen || compact) && 'compact'}>
        <IconButton
          type="button"
          data-testid="google-auth-button"
          disabled={isGoogleSubmitting}
          onClick={onGoogleClick}
        >
          <Wrapper>
            {isGoogleSubmitting && <LoadingSpinner color="#72D4BA" className="Button-loadingSpinner" />}
            {!isGoogleSubmitting && (
              <>
                <img height={17} alt="Google" src="/images/icons/google.svg" />
                <span>{(ignoreSmallScreen ? false : smallScreen || compact) ? 'Google' : 'Continue with Google'}</span>
              </>
            )}
          </Wrapper>
        </IconButton>

        <IconButton
          type="button"
          data-testid="facebook-auth-button"
          disabled={isFacebookSubmitting}
          onClick={onFacebookClick}
        >
          <Wrapper>
            {isFacebookSubmitting && <LoadingSpinner color="#72D4BA" className="Button-loadingSpinner" />}
            {!isFacebookSubmitting && (
              <>
                <img height={17} alt="Facebook" src="/images/icons/facebook.svg" />
                <span>
                  {(ignoreSmallScreen ? false : smallScreen || compact) ? 'Facebook' : 'Continue with Facebook'}
                </span>
              </>
            )}
          </Wrapper>
        </IconButton>

        <IconButton type="button" data-testid="apple-auth-button" disabled={isAppleSubmitting} onClick={onAppleClick}>
          <Wrapper>
            {isAppleSubmitting && <LoadingSpinner color="#72D4BA" className="Button-loadingSpinner" />}
            {!isAppleSubmitting && (
              <>
                <img height={17} alt="Apple" src="/images/icons/apple.svg" />
                <span>{(ignoreSmallScreen ? false : smallScreen || compact) ? 'Apple' : 'Continue with Apple'}</span>
              </>
            )}
          </Wrapper>
        </IconButton>
      </ButtonWrapper>
    </ButtonsWrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  margin: auto;
  span,
  img {
    margin: auto 0;
  }
  span {
    margin-left: 10px;
    ${mediaMSmMax} {
      padding-top: 3px;
    }
  }
`;

const IconButton = styled.button`
  width: 100%;
  border: 1px solid #8e9697;
  border-radius: 6px;
  height: 55px;
  // ${mediaMSmMax} {
  //   height: 70px;
  // }
  &:disabled {
    opacity: 0.7;
  }
  font-weight: 500;
  font-size: 16px;
  display: flex;
`;

const Or = styled.div`
  display: flex;
  color: #8e9697;
  opacity: 0.5;
  font-weight: 500;
  font-size: 14px;
  padding: 20px 0;
  span {
    font-size: 12px;
    width: auto;
    white-space: pre;
    text-align: center;
    padding: 0 10px;
  }
  hr {
    margin-top: 8px;
    width: 100%;
    height: 0px;
  }
`;

const ButtonWrapper = styled(FlexCol)`
  &.compact {
    display: flex;
    flex-flow: row;
    gap: 20px;
    ${Wrapper} {
      font-size: 14px;
      flex-flow: column;
      span {
        margin: auto;
      }
    }
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-flow: column;
`;

export default SocialLoginButtonsV2;
